/**
 * V2 browser data boundary for service catalog, customer subscriptions,
 * WhiteHaX execution context, and aggregated service-report operations.
 */
import { customerDetails, customers, reportRuns, serviceDefinitions } from "./mockData";

const USE_MOCKS = process.env.REACT_APP_MSSP_USE_MOCKS !== "false";
const API_ROOT = "/api/internal/mssp";
const pause = (value) => new Promise((resolve) => setTimeout(() => resolve(structuredClone(value)), 180));

async function request(path, options = {}) {
  // TODO_BACKEND_API: Replace with WhiteHaX's CSRF-aware authenticated fetch.
  const response = await fetch(`${API_ROOT}${path}`, {
    credentials: "include",
    headers: { "Content-Type": "application/json", ...(options.headers || {}) },
    ...options,
  });
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body.detail || body.message || `Request failed (${response.status})`);
  }
  return response.status === 204 ? null : response.json();
}

export async function listCustomers() {
  return USE_MOCKS ? pause(customers) : request("/customers/");
}

export async function listServiceDefinitions() {
  return USE_MOCKS ? pause(serviceDefinitions) : request("/service-definitions/");
}

export async function getCustomer(licenseId) {
  if (!USE_MOCKS) return request(`/customers/${licenseId}/`);
  const customer = customers.find((item) => String(item.licenseId) === String(licenseId));
  if (!customer) throw new Error("Customer not found");
  const detail = customerDetails[licenseId] || {
    users: [], endpoints: [], profiles: [], jobSchedules: [], testRuns: [], reports: [], services: [],
  };
  return pause({ ...customer, ...detail });
}

export async function saveCustomerService(customerServiceId, changes) {
  if (!USE_MOCKS) {
    return request(`/customer-services/${customerServiceId}/`, { method: "PUT", body: JSON.stringify(changes) });
  }
  // Update the fixture in place so switching services demonstrates independent
  // management modes and report policies during one preview session.
  for (const detail of Object.values(customerDetails)) {
    const index = detail.services?.findIndex((service) => service.id === customerServiceId) ?? -1;
    if (index >= 0) {
      detail.services[index] = { ...detail.services[index], ...changes };
      return pause(detail.services[index]);
    }
  }
  throw new Error("Customer service not found");
}

export async function generateServiceReport(customerServiceId) {
  if (USE_MOCKS) return pause({ reportRunId: Date.now(), customerServiceId, status: "queued" });
  // TODO_REPORT: The backend aggregates linked JOB/SCAN_RESULT evidence.
  return request(`/customer-services/${customerServiceId}/reports/generate/`, { method: "POST", body: "{}" });
}

export async function listReportRuns() {
  return USE_MOCKS ? pause(reportRuns) : request("/report-runs/");
}

export async function retryReportRun(reportRunId) {
  return USE_MOCKS
    ? pause({ id: reportRunId, status: "queued" })
    : request(`/report-runs/${reportRunId}/retry/`, { method: "POST", body: "{}" });
}
